import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
  ResponseArtifactDTO,
  MultipleChoiceArtifact,
  ArtifactChoice,
} from '@dreamai/types';
import api from '../../api';
import { ReactComponent as ChevronLeftIcon } from '../../icons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from '../../icons/chevron-right.svg';
import ImageCard from '../shared/ImageCard';
import FileLink from '../shared/FileLink';
import MultipleChoiceMapView from './sidebar/MultipleChoiceMapView';
import './inline-multiple-choice.scss';

interface InlineMultipleChoiceProps {
  artifactId: string;
  threadId: string;
}

function formatPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

/** Build a merged fields array with a synthesized Price field from dedicated pricing. */
function getFieldsWithPrice(
  choice: ArtifactChoice,
): { label: string; value: string }[] {
  const fields: { label: string; value: string }[] = [];

  if (choice.definitivePrice != null && choice.priceCurrency) {
    fields.push({
      label: 'Price',
      value: formatPrice(choice.definitivePrice, choice.priceCurrency),
    });
  } else if (choice.priceEstimate != null && choice.priceCurrency) {
    fields.push({
      label: 'Price',
      value: `~${formatPrice(choice.priceEstimate, choice.priceCurrency)}`,
    });
  } else if (choice.approxPriceRange && choice.priceCurrency) {
    fields.push({
      label: 'Price',
      value: `${formatPrice(choice.approxPriceRange[0], choice.priceCurrency)} – ${formatPrice(choice.approxPriceRange[1], choice.priceCurrency)}`,
    });
  }

  if (choice.fields) {
    for (const f of choice.fields) {
      fields.push({ label: f.label, value: f.value });
    }
  }

  return fields;
}

const InlineMultipleChoice: React.FC<InlineMultipleChoiceProps> = ({
  artifactId,
  threadId,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [artifact, setArtifact] = useState<ResponseArtifactDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollArrows();
    el.addEventListener('scroll', updateScrollArrows, { passive: true });
    const ro = new ResizeObserver(updateScrollArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollArrows);
      ro.disconnect();
    };
  }, [updateScrollArrows, artifact]);

  const scrollBy = useCallback((dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 272, behavior: 'smooth' });
  }, []);

  // Fetch the full artifact on mount
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
        }>(`/response-artifacts/artifact/${artifactId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data.success) {
          setArtifact(res.data.artifact);
        }
      } catch (err) {
        console.error('InlineMultipleChoice: fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [artifactId, getAccessTokenSilently]);

  const mcArtifact = artifact
    ? (artifact as unknown as MultipleChoiceArtifact)
    : null;

  if (loading) return null;
  if (!artifact || !mcArtifact) return null;

  const isActioned = artifact.status === 'actioned';
  if (isActioned) {
    return (
      <div className='inline-mc'>
        <div className='imc-done'>✓ Done</div>
      </div>
    );
  }

  const choices = mcArtifact.choices || [];
  const isMap = mcArtifact.view === 'map';
  const isImageHeavy = choices.some((c) => c.imageHeavy);

  const renderCompactCard = (choice: ArtifactChoice) => (
    <div key={choice.choiceId} className='imc-card'>
      {choice.imageUrl && (
        <div className='imc-card-image'>
          <img
            src={choice.imageUrl}
            alt={choice.choiceTitle}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
      <div className='imc-card-body'>
        <b>{choice.choiceTitle}</b>
        {choice.choiceBlurb && (
          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
            {choice.choiceBlurb}
          </ReactMarkdown>
        )}
        {(() => {
          const allFields = getFieldsWithPrice(choice);
          return allFields.length > 0 ? (
            <div className='imc-card-fields'>
              {allFields.map((field, idx) => (
                <div key={idx} className='imc-card-field'>
                  <span className='field-label'>{field.label}</span>
                  <span className='field-value'>{field.value}</span>
                </div>
              ))}
            </div>
          ) : null;
        })()}
        {choice.links && choice.links.length > 0 && (
          <div className='imc-card-links'>
            {choice.links.map((link, idx) => (
              <FileLink key={idx} url={link.url}>
                {link.label}
              </FileLink>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderImageHeavyCard = (choice: ArtifactChoice) => (
    <div key={choice.choiceId} className='imc-full-card'>
      <ImageCard
        id={choice.choiceId}
        title={choice.choiceTitle}
        blurb={choice.choiceBlurb}
        imageUrl={choice.imageUrl}
        additionalImages={choice.additionalImages}
        fields={getFieldsWithPrice(choice)}
        links={choice.links}
        selected={false}
      />
    </div>
  );

  return (
    <div className='inline-mc'>
      {/* Title */}
      {mcArtifact.title && <div className='imc-title'>{mcArtifact.title}</div>}

      {/* Map view: show inline map */}
      {isMap ? (
        <div className='imc-map-container'>
          <MultipleChoiceMapView
            choices={choices}
            selectedChoices={[]}
            onChoiceSelect={() => {}}
            readOnly
            height='100%'
          />
        </div>
      ) : isImageHeavy ? (
        <div className='imc-vertical-list'>
          {choices.map(renderImageHeavyCard)}
        </div>
      ) : (
        <div className='imc-scroll-wrapper'>
          {canScrollLeft && (
            <button
              className='imc-scroll-arrow left'
              onClick={() => scrollBy(-1)}
              aria-label='Scroll left'
            >
              <ChevronLeftIcon />
            </button>
          )}
          <div className='imc-scroll-track' ref={scrollRef}>
            {choices.map(renderCompactCard)}
          </div>
          {canScrollRight && (
            <button
              className='imc-scroll-arrow right'
              onClick={() => scrollBy(1)}
              aria-label='Scroll right'
            >
              <ChevronRightIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default InlineMultipleChoice;
